import { MagicWantIcon } from 'assets/images/icons';
import { Button } from 'common/ui/button';
import { IconButton } from 'common/ui/icon-button';
import { Tooltip } from 'common/ui/tooltip';
import { Faqs, Pros } from 'context/page-form-context';
import {
  convertValueToFaqs,
  convertValueToPros,
  MelChatModal,
  MelFields,
  useMelContext,
} from 'modules/mel-ai';
import { useMemo, useState } from 'react';

import styles from './mel-regen-button.module.scss';

interface MelRegenButtonProps {
  fieldName?: MelFields;
  fieldRecord?: Faqs | Pros;
  fieldValue?: string;
  onApplyMel?: (values: string | Pros | Faqs) => void;
  regenAll?: boolean;
}

const MelRegenButton = ({
  fieldName,
  fieldRecord,
  fieldValue,
  onApplyMel,
  regenAll,
}: MelRegenButtonProps) => {
  const isBottomLine = fieldName === MelFields.BottomLine;
  const isBuyingGuide = fieldName === MelFields.BuyingGuide;
  const isFaqs = fieldName === MelFields.Faqs;
  const isPros = fieldName === MelFields.Pros;
  const isSummary = fieldName === MelFields.Summary;

  const {
    handleRegeneratePage,
    selectedVariant,
    setCurrentFieldName,
    setSelectedVariant,
    variantsMap,
  } = useMelContext();

  const [aiVariantModalOpen, setAiVariantModalOpen] = useState<boolean>(false);

  const variantCount = useMemo(() => {
    if (fieldName) return variantsMap[fieldName].length;
    return 0;
  }, [fieldName, variantsMap]);

  const onOpen = () => {
    setCurrentFieldName(fieldName);
    setAiVariantModalOpen(true);
  };

  const onClose = () => {
    setSelectedVariant(null);
    setAiVariantModalOpen(false);
  };

  const onApply = () => {
    if (isBottomLine || isBuyingGuide || isSummary) onApplyMel(selectedVariant.value);
    if (isFaqs && onApplyMel) onApplyMel(convertValueToFaqs(selectedVariant.values as string));
    if (isPros && onApplyMel) onApplyMel(convertValueToPros(selectedVariant.value));

    onClose();
  };

  if (regenAll) {
    return (
      <Button
        className={styles.regenAll}
        fullWidth
        iconStart={<MagicWantIcon />}
        onClick={() => handleRegeneratePage()}
        outlined
        size="small"
        variant="secondary"
      >
        Generate All Contents
      </Button>
    );
  }

  return (
    <>
      <Tooltip title={`${variantCount}`}>
        <IconButton
          iconProps={{ showBorder: true, size: 'md', variant: 'secondary' }}
          onClick={() => onOpen()}
          type="icon"
        >
          <MagicWantIcon />
        </IconButton>
      </Tooltip>

      {aiVariantModalOpen && (
        <MelChatModal
          currentFieldName={fieldName}
          onApply={onApply}
          onClose={onClose}
          open={aiVariantModalOpen}
          originalContent={fieldValue ?? fieldRecord}
        />
      )}
    </>
  );
};

export { MelRegenButton };
